import { create } from 'zustand'
import { authService } from '../services/auth'
import { hubService } from '../services/hub'
import { toast } from 'react-hot-toast'
import { monitoring } from '../utils/monitoring'
import type { User } from '../types'

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  checkAuth: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setUser: (user: User) => void
  setToken: (token: string) => void
  setRefreshToken: (refreshToken: string) => void
  clearError: () => void
}

function loadAuthFromStorage() {
  const token = localStorage.getItem('authToken')
  const refreshToken = localStorage.getItem('refreshToken')
  const userStr = localStorage.getItem('user')
  let user: User | null = null
  try {
    if (userStr) user = JSON.parse(userStr)
  } catch {}
  return {
    user,
    token,
    refreshToken,
    isAuthenticated: !!token,
    isLoading: false,
    error: null,
  }
}

export const useAuthStore = create<AuthState>()(
  (set) => ({
    ...loadAuthFromStorage(),

    checkAuth: async () => {
      const token = authService.getToken()
      if (!token) {
        set({ isAuthenticated: false, user: null })
        return
      }
      set({ isLoading: true })
      try {
        const user = await authService.getCurrentUser()
        set({ user, token, isAuthenticated: true, isLoading: false })
        localStorage.setItem('user', JSON.stringify(user))
        monitoring.setUserId(user.id)
        monitoring.trackEvent('auth_check_success')
      } catch (error) {
        authService.clearTokens()
        set({ user: null, token: null, refreshToken: null, isAuthenticated: false, isLoading: false })
        localStorage.removeItem('user')
      }
    },

    login: async (email: string, password: string) => {
      set({ isLoading: true, error: null })
      try {
        const { user, token, refreshToken } = await authService.login({ email, password })
        authService.setToken(token)
        authService.setRefreshToken(refreshToken)
        set({ user, token, refreshToken, isAuthenticated: true, isLoading: false })
        localStorage.setItem('user', JSON.stringify(user))
        monitoring.setUserId(user.id)
        monitoring.trackEvent('login_success', { method: 'email' })
        monitoring.trackConversion('user_login')
        toast.success('Logged in successfully!')
        // Remove window.location.href to prevent page reload and flashing
        // Navigation will be handled by the LoginForm component's useEffect
      } catch (error: any) {
        let friendly = 'Login failed. Please check your credentials.'
        if (error?.response?.status === 401) {
          friendly = 'Invalid email or password.'
        }
        set({ 
          isLoading: false, 
          error: friendly
        })
        toast.error(friendly)
      }
    },

    register: async (name: string, email: string, password: string) => {
      set({ isLoading: true, error: null })
      try {
        const { user, token, refreshToken } = await authService.register({ name, email, password, confirmPassword: password, terms: true })
        authService.setToken(token)
        authService.setRefreshToken(refreshToken)
        set({ user, token, refreshToken, isAuthenticated: true, isLoading: false })
        localStorage.setItem('user', JSON.stringify(user))
        // Automatically create a default hub for the new user
        try {
          const defaultHub = await hubService.createHub({
            name: `${name}'s Hub`,
            description: `Welcome to ${name}'s communication hub!`,
            settings: { privacyLevel: 'private' }
          })
          toast.success(`Account created successfully! Your hub "${defaultHub.name}" has been created.`)
        } catch (hubError: any) {
          // If hub creation fails, still show success but warn about hub
          console.warn('Failed to create default hub:', hubError)
          toast.success('Account created successfully! You can create your hub later.')
        }
        // Remove window.location.href to prevent page reload and flashing
        // Navigation will be handled by the RegisterForm component's useEffect
      } catch (error: any) {
        let friendly = 'Registration failed. Please try again.'
        if (error?.response?.status === 409) {
          friendly = 'An account with this email already exists. Please sign in or use a different email.'
        }
        set({ 
          isLoading: false, 
          error: friendly
        })
        toast.error(friendly)
      }
    },

    logout: async () => {
      if (typeof window !== 'undefined') {
        localStorage.clear();
      }
      set({ isLoading: true })
      try {
        await authService.logout()
      } catch (error) {
        // Continue with logout even if API call fails
      } finally {
        authService.clearTokens()
        set({ 
          user: null, 
          token: null, 
          refreshToken: null,
          isAuthenticated: false, 
          isLoading: false 
        })
        localStorage.removeItem('user')
        toast.success('Logged out successfully')
      }
    },

    setUser: (user: User) => {
      set({ user })
      localStorage.setItem('user', JSON.stringify(user))
    },

    setToken: (token: string) => {
      set({ token, isAuthenticated: true })
      authService.setToken(token)
    },
    setRefreshToken: (refreshToken: string) => {
      set({ refreshToken })
      authService.setRefreshToken(refreshToken)
    },

    clearError: () => {
      set({ error: null })
    },
  })
) 